"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Flame, Cpu, Layers, CircleCheck as CheckCircle2, ArrowUpRight, Leaf, FileCheck2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const solutions = [{
  id: "sncr",
  title: "Sistemas SNCR (Não-Catalítica)",
  description: "Injeção precisa de ureia ou amónia diretamente na câmara de combustão, otimizando o tamanho de gota para máxima reação térmica sem catalisador.",
  tag: "Alta Temperatura",
  icon: Flame,
  points: ["Atomização homogénea sem molhagem de paredes", "Redução típica de 40% a 70% de NOx", "Manutenção simplificada e baixo custo operacional"]
}, {
  id: "scr",
  title: "Sistemas SCR (Catalítica Seletiva)",
  description: "Distribuição uniforme de reagente a montante do leito catalítico, garantindo dosagem estequiométrica exata com desvio mínimo de amónia (ammonia slip).",
  tag: "Elevada Eficiência",
  icon: Layers,
  points: ["Eficiência de redução superior a 90%", "Lanças de injeção especiais resistentes à corrosão", "Modulação rápida para cargas variáveis"]
}, {
  id: "control",
  title: "Controlo e Automação AutoJet",
  description: "Painéis de comando dedicados com controlo em circuito fechado, integrando dados de CEMS e temperatura para ajuste de caudal em milissegundos.",
  tag: "Tecnologia Integrada",
  icon: Cpu,
  points: ["Doseamento proporcional ao caudal de gases", "Comunicação direta com sistemas SCADA/DCS", "Proteção de bicos com purga de ar automática"]
}];
const standards = [{
  id: "ied",
  title: "Diretiva de Emissões Industriais (IED)",
  code: "Diretiva 2010/75/UE",
  summary: "Cumprimento estrito dos valores-limite de emissão (VLE) em grandes instalações de combustão, cimenteiras e incineradores."
}, {
  id: "bat",
  title: "Conclusões BAT / BREF",
  code: "Melhores Técnicas Disponíveis",
  summary: "Implementação de sistemas de injeção validados pelos documentos de referência europeus para mitigação de poluentes atmosféricos."
}, {
  id: "monitoring",
  title: "Controlo de 'Ammonia Slip'",
  code: "< 2 a 5 mg/Nm³",
  summary: "Prevenção da formação de sais de amónio a jusante, protegendo permutadores de calor e filtros de mangas."
}];
export default function NoxControl() {
  return <section id="noxcontroloverview" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Controlo de Emissões & Sustentabilidade
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Sistemas de controlo de emissões NOx
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Engenharia de precisão para injeção de ureia e amónia em processos térmicos. 
            Soluções completas com suporte técnico local da Eurocontrol em parceria com a Spraying Systems Co.
          </motion.p>
        </div>

        {/* Main Solution Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {solutions.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={index} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.12,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="p-2.5 rounded-lg bg-secondary/10 text-secondary">
                          <Icon className="size-6" />
                        </div>
                        <Badge variant="outline" className="text-muted-foreground border-border text-xs">
                          {item.tag}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-card-foreground font-default">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm leading-normal text-muted-foreground">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-6">
                      <Separator className="my-3 bg-border/60" />
                      <ul className="space-y-2.5 mt-4">
                        {item.points.map((point, pIndex) => <li key={pIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-card-foreground/90">
                            <CheckCircle2 className="size-4 text-chart-3 shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-0">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to="/TechnicalSolutionsPage">
                        <span>Ver especificações</span>
                        <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Regulatory & Compliance Section Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-5 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground text-xs font-semibold">
                <ShieldCheck className="size-4" />
                Conformidade Ambiental
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight text-primary-foreground">
                Normas e limites regulamentares industriais
              </h3>
              <p className="text-sm sm:text-base text-primary-foreground/85 leading-relaxed">
                Asseguramos que o seu sistema cumpre com precisão as metas de emissão estipuladas pelas licenças ambientais em Portugal e na União Europeia.
              </p>
              <div className="pt-2">
                <Button asChild variant="secondary" size="lg" className="shadow-sm">
                  <Link to="/ContactsPage">
                    Consulte-nos para diagnóstico
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {standards.map((std, sIndex) => <div key={std.id} data-index={sIndex} className="rounded-xl bg-primary-foreground/5 border border-primary-foreground/10 p-4 sm:p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-chart-4 mb-2">
                      <FileCheck2 className="size-4 shrink-0" />
                      <span className="text-xs font-mono font-medium tracking-wide">
                        {std.code}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-primary-foreground mb-1.5 font-default">
                      {std.title}
                    </h4>
                    <p className="text-xs text-primary-foreground/80 leading-relaxed">
                      {std.summary}
                    </p>
                  </div>
                </div>)}
            </div>

          </div>
        </motion.div>

        {/* Environmental Commitment Note */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-12 flex flex-wrap items-center justify-between gap-4 text-xs sm:text-sm text-muted-foreground border-t border-border pt-6">
          <div className="flex items-center gap-2">
            <Leaf className="size-4 text-chart-3" />
            <span>Redução de pegada ecológica e otimização do consumo de reagentes químicos.</span>
          </div>
          <div>
            <Link to="/SustainabilityPage" className="inline-flex items-center gap-1 font-medium text-foreground hover:text-secondary underline underline-offset-4">
              Consulte a nossa política de sustentabilidade
              <ArrowUpRight className="size-3.5" />
            </Link>
          </div>
        </motion.div>

      </div>
    </section>;
}